<script setup lang="ts">
export interface UserMenuProps {
  fullName?: string;
  withThemeSwitcher?: boolean;
  logoInitialsSize?: number;
}

const { t } = useI18n();
const emit = defineEmits(["logout"]);
const props = defineProps<UserMenuProps>();
const appConfig = useRuntimeConfig().public;

const isLeagueView = appConfig.APP_CUSTOMER.enable_leagues_view;

const hasClips = appConfig.APP_CUSTOMER.web_sdk.enable_clipping;
</script>

<template>
  <UMenu>
    <template #trigger="{ toggle }">
      <BaseButton name="user-menu" type="icon" title="Menu" @click="toggle">
        <IconLogoInitials
          v-if="props.fullName"
          :title="props.fullName"
          :width="props.logoInitialsSize"
          :height="props.logoInitialsSize"
        />
      </BaseButton>
    </template>
    <template #default>
      <div v-auto-animate class="w-[18rem] bg-white dark:bg-neutral-dark-700">
        <UList>
          <UListItem size="md" class="!p-0">
            <BaseButton
              class="text-link w-full flex items-center gap-3" type="icon" tag="nuxt-link"
              name="header-account-link" :to="{ path: '/account/general/' }"
            >
              <div class="w-full flex items-center gap-3 px-4">
                <IconHeaderAccount class="text-[var(--base-icon-color)]" />
                <span class="text-[var(--base-secondary-text-color)] truncate">{{ t('labels.account') }}</span>
              </div>
            </BaseButton>
          </UListItem>
          <UListItem size="md" class="!p-0">
            <BaseButton
              class="text-link w-full flex items-center gap-3" type="icon" tag="nuxt-link"
              name="header-following-link" :to="{ path: isLeagueView ? '/following/league/' : '/following/category/' }"
            >
              <div class="w-full flex items-center gap-3 px-4">
                <IconHeaderManageFollowing class="text-[var(--base-icon-color)]" />
                <span class="text-[var(--base-secondary-text-color)] truncate">{{
                  t('labels.manage_following')
                }}</span>
              </div>
            </BaseButton>
          </UListItem>
          <UListItem size="md" class="!p-0">
            <BaseButton
              class="text-link w-full flex items-center gap-3" type="icon" tag="nuxt-link"
              name="header-videos-link" :to="{ path: '/selected/videos/' }"
            >
              <div class="w-full flex items-center gap-3 px-4">
                <IconHeaderSelectedVideos class="text-[var(--base-icon-color)]" />
                <span class="text-[var(--base-secondary-text-color)] truncate">{{ t('labels.selected_videos') }}</span>
              </div>
            </BaseButton>
          </UListItem>
          <UListItem v-if="hasClips" size="md" class="!p-0">
            <BaseButton
              class="text-link w-full flex items-center gap-3" type="icon" tag="nuxt-link"
              name="header-clips-link" :to="{ path: '/selected/clips/' }"
            >
              <div class="w-full flex items-center gap-3 px-4">
                <IconHeaderMyClipsHighlights class="text-[var(--base-icon-color)]" />
                <span class="text-[var(--base-secondary-text-color)] truncate">{{
                  t('labels.my_clips_and_highlights')
                }}</span>
              </div>
            </BaseButton>
          </UListItem>
          <UListItem size="md" class="!p-0">
            <BaseButton
              class="text-secondary w-full text-link flex items-center gap-3" type="icon"
              name="header-logout-button" @click.prevent="emit('logout')"
            >
              <div class="w-full flex items-center gap-3 px-4">
                <IconHeaderLogout />
                <span class="text-error">{{ t('labels.logout') }}</span>
              </div>
            </BaseButton>
          </UListItem>
          <template v-if="withThemeSwitcher">
            <UDivider lighter />
            <div class="flex items-center justify-center w-full pt-4 pb-2">
              <BaseThemeSwitcher has-labels />
            </div>
          </template>
        </UList>
      </div>
    </template>
  </UMenu>
</template>
